/*
 * Baksteendirect — card-swatch.js
 * Standalone <product-card> element recreating Ignite's swatch hover-image-swap.
 * Owned by the card build agent. Loaded (deferred) from snippets/card-product.liquid.
 *
 * On hovering / focusing / tapping a swatch trigger that carries data-variant-media,
 * the matching variant image is injected over the primary card image and revealed via
 * the .show-swatch-image / .swatch-hover-image CSS contract already shipped in
 * component-card.css (lines ~448-462). Mirrors Ignite/assets/global.js ProductCard,
 * trimmed to the swatch concern only (no quick-buy hover button, no bulk form).
 */
if (!customElements.get('product-card')) {
  class ProductCard extends HTMLElement {
    constructor() {
      super();
      this.showSwatchHandler = this.showSwatchImage.bind(this);
      this.hideSwatchHandler = this.hideSwatchImage.bind(this);
      this.mouseEnterHandler = this.handleMouseEnter.bind(this);
      this.swatchesPreloaded = false;
    }

    connectedCallback() {
      this.swatchTriggers = this.querySelectorAll('.swatch-trigger[data-variant-media]');
      if (this.swatchTriggers.length === 0) return;

      this.createSwatchHoverImage();
      this.addEventListener('mouseenter', this.mouseEnterHandler, { passive: true });
      this.addEventListener('mouseover', this.showSwatchHandler, { passive: true });
      this.addEventListener('mouseleave', this.hideSwatchHandler, { passive: true });
      this.addEventListener('focusin', this.showSwatchHandler, { passive: true });
      this.addEventListener('focusout', this.hideSwatchHandler, { passive: true });
      this.addEventListener('touchstart', this.showSwatchHandler, { passive: true });
    }

    disconnectedCallback() {
      this.removeEventListener('mouseenter', this.mouseEnterHandler);
      this.removeEventListener('mouseover', this.showSwatchHandler);
      this.removeEventListener('mouseleave', this.hideSwatchHandler);
      this.removeEventListener('focusin', this.showSwatchHandler);
      this.removeEventListener('focusout', this.hideSwatchHandler);
      this.removeEventListener('touchstart', this.showSwatchHandler);
    }

    createSwatchHoverImage() {
      this.mediaContainer = this.querySelector('.card__media .media');
      if (!this.mediaContainer) return;
      const existingImg = this.mediaContainer.querySelector('img');
      this.swatchHoverImg = document.createElement('img');
      this.swatchHoverImg.className = existingImg ? existingImg.className : '';
      this.swatchHoverImg.classList.remove('card__image-hover');
      this.swatchHoverImg.classList.add('swatch-hover-image');
      this.swatchHoverImg.style.display = 'none';
      this.swatchHoverImg.alt = '';
      this.swatchHoverImg.setAttribute('aria-hidden', 'true');
      this.swatchHoverImg.setAttribute('loading', 'lazy');
      this.mediaContainer.appendChild(this.swatchHoverImg);
    }

    handleMouseEnter() {
      if (!this.swatchesPreloaded) this.preloadSwatchImages();
    }

    showSwatchImage(event) {
      const trigger = event.target.closest('.swatch-trigger[data-variant-media]');
      if (!trigger || !this.swatchHoverImg) return;
      const imageUrl = trigger.dataset.variantMedia;
      if (!imageUrl) return;
      this.swatchHoverImg.src = imageUrl;
      this.swatchHoverImg.style.display = 'block';
      this.swatchHoverImg.classList.add('show-swatch-image');
    }

    hideSwatchImage() {
      if (!this.swatchHoverImg) return;
      this.swatchHoverImg.style.display = 'none';
      this.swatchHoverImg.classList.remove('show-swatch-image');
    }

    preloadSwatchImages() {
      this.swatchTriggers.forEach((trigger) => {
        if (trigger.dataset.variantMedia) {
          const img = new Image();
          img.src = trigger.dataset.variantMedia;
        }
      });
      this.swatchesPreloaded = true;
    }
  }
  customElements.define('product-card', ProductCard);
}
